<template>
  <div class="mb-3" v-if="showComponent">
    <label
      v-if="label"
      :title="label"
      class="text-truncate"
      @click="$refs.treeselect.openMenu()"
    >
      {{ label }} <small v-if="isRequired">*</small>
    </label>
    <font-awesome-icon
      id="font-awesome-icon-edit"
      icon="edit"
      size="sm"
      class="clickable float-right mt-1"
      :class="{ 'ml-2': help }"
      v-if="showEditButton"
      @click="$emit('edit-button-clicked')"
      />
    <font-awesome-icon
      icon="question-circle"
      size="sm"
      class="clickable float-right mt-1"
      v-if="help"
      v-b-tooltip.hover="help"
    />
    <lm-dropdown
      v-if="isEditableByPickedUser"
      class="float-right"
      :tooltip="!disableEditors ? 'Selecciona quién edita este campo' : null"
      template="user-list"
      @input="$emit('editor-change', $event)"
      :value="collaborator"
      :disabled="disableEditors"
      closeAfterPick
    >
      <div class="d-flex align-items-center gap-2">
        {{ collaborator.name || '' }}
        <a-icon type="edit" v-if="!disableEditors" />
      </div>
    </lm-dropdown>
    <Treeselect
      ref="treeselect"
      :placeholder="placeholder"
      :multiple="multiple"
      :normalizer="normalizer"
      :valueFormat="valueFormat"
      :disabled="disabled || isDisabledByChildOf"
      :clearable="clearable"
      :class="{'is_invalid': validation.hasError}"

      :default-expand-level="defaultExpandLevel"

      :async="async"
      :autoLoadRootOptions="autoLoadRootOptions"
      :load-options="loadOptions"
      :options="optionsData"
      :valueConsistsOf="valueConsistsOf"

      noOptionsText="No hay opciones disponibles"
      noResultsText="No se encontraron resultados"
      loadingText="Cargando opciones..."
      searchPromptText="Escribe para buscar..."

      :disable-branch-nodes="disableBranchNodes"
      :show-count="showCount"

      :value="data"
      @input="$emit('input', $event || null)"
      @select="$emit('select', $event)"
      @deselect="$emit('deselect', $event)"
      @search-change="$emit('search-change', $event)"
      @open="$emit('open', $event)"
    />
    <p class="d-block invalid-feedback" v-if="validation.hasError"> {{ showValidationErrorMessage }} </p>
  </div>
</template>

<script>
// Component
import Treeselect, { LOAD_ROOT_OPTIONS, ASYNC_SEARCH } from '@lumile/vue-treeselect'
import '@lumile/vue-treeselect/dist/vue-treeselect.css'

// Utils
import LmCommonProps from '@/mixins/LmCommonProps'
import commonsApi from '@/api/commons'

export default {
  mixins: [LmCommonProps],
  components: {
    Treeselect
  },
  props: {
    valueFormat: {
      type: String,
      default: 'object'
    },
    placeholder: {
      type: String,
      default: 'Seleccione una opción'
    },
    multiple: {
      type: Boolean,
      default: false
    },
    valueConsistsOf: {
      type: String,
      default: 'LEAF_PRIORITY'
    },
    options: {
      type: Array,
      default: null
    },
    // Carga las opciones al desplegar el componente
    // Si es true, las opciones se cargan al montar el componente
    // NOTA: Es una mejora de performance dejarlo en false pero en modo edición no se muestran las opciones
    autoLoadRootOptions: {
      type: Boolean,
      default: true
    },
    pathToGetOptions: {
      type: [String, Array],
      default: null
    },
    multipleOriginPaths: {
      type: Boolean,
      default: false
    },
    defaultValue: {
      type: [Object, Array],
      default: null
    },
    allSelectedAsDefault: {
      type: Boolean,
      default: false
    },
    childrenOf: {
      type: String,
      default: null
    },
    async: {
      type: Boolean,
      default: false
    },
    pathToGetAsyncOptions: {
      type: [String, Array],
      default: null
    },
    defaultExpandLevel: {
      type: [String, Number],
      default: 0
    },
    clearable: {
      type: Boolean,
      default: false
    },
    disableBranchNodes: {
      type: Boolean,
      default: false
    },
    showCount: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      normalizer (node) {
        return {
          id: node.id,
          label: node.name
        }
      },
      optionsFromPath: null
    }
  },
  computed: {
    data () { // Array de Objs o Obj en cuestion (seteado valueFormat = object)
      return this.value || null
    },
    optionsData () {
      return this.options || this.optionsFromPath || null
    },

    isDisabledByChildOf () {
      if (this.context === null) {
        return false
      }
      return !!(this.childrenOf && !this.context[this.childrenOf])
    }
  },
  watch: {
    // Si el path para obtener las opciones cambia, vuelvo a cargar las opciones
    async pathToGetOptions (val) {
      if (val || val?.length) {
        try {
          await this.loadOptions({ action: 'LOAD_ROOT_OPTIONS' })
          if (this.allSelectedAsDefault) {
            this.setAllValuesSelected()
          }
        } catch (error) {
          console.log(error)
        }
      } else {
        this.optionsFromPath = null
      }
    },
    // Todos los valores seleccionados por defecto
    allSelectedAsDefault (val) {
      if (val) {
        this.setAllValuesSelected()
      }
    }
  },
  methods: {
    // Cargo las opciones dinámicamente (solo si options === null)
    async loadOptions ({ action, searchQuery, callback }) {
      if (action === LOAD_ROOT_OPTIONS) {
        if (this.pathToGetOptions && this.childrenOf === null) {
          try {
            // Único path para obtener opciones
            if (this.pathToGetOptions && typeof this.pathToGetOptions === 'string') {
              const { data } = await commonsApi.axiosGetWrapper(this.pathToGetOptions)
              this.optionsFromPath = data.items
              return
            }
            // Múltiples paths para obtener opciones
            if (Array.isArray(this.pathToGetOptions) && this.pathToGetOptions.length) {
              this.optionsFromPath = []
              const paths = this.pathToGetOptions.map(item => item.path)
              const promises = paths.map(path => commonsApi.axiosGetWrapper(path))
              const options = await Promise.all(promises)
              for (const [index, node] of this.pathToGetOptions.entries()) {
                const children = options[index].data.items.map(i => {
                  return {
                    id: i.id,
                    name: i.name,
                    branchId: node.id,
                    branchName: node.name
                  }
                })
                const option = {
                  id: node.id,
                  name: node.name,
                  children: children
                }
                this.optionsFromPath.push(option)
              }
              return
            }
          } catch (error) {
            console.error(error)
          }
        }
      }
      if (action === ASYNC_SEARCH) {
        if (this.async) {
          // Único path para obtener opciones
          if (this.pathToGetAsyncOptions && typeof this.pathToGetAsyncOptions === 'string') {
            const response = await commonsApi.axiosGetWrapper(this.pathToGetAsyncOptions, {
              term: searchQuery || ''
            })
            const nodes = response.data.items
            callback(null, nodes)
          }
          // Múltiples paths para obtener opciones
          if (Array.isArray(this.pathToGetAsyncOptions) && this.pathToGetAsyncOptions.length) {
            const ids = this.pathToGetAsyncOptions.map(path => path.id)
            // TODO. Por cada item del array podria tener un path distinto
            // NOTA. Podria mapear los paths únicos y ejecutar la busqueda por cada uno (ver)
            const response = await commonsApi.axiosGetWrapper(`/customercodificatorbuilder/predictive/codificators/`, {
              ids: ids,
              term: searchQuery
            })
            const nodes = []
            if (response.data.items.length) {
              this.pathToGetAsyncOptions.forEach(path => {
                const children = response.data.items.filter(option => option.codificatorBuilderId === path.id)
                if (children.length) {
                  const nodeChildren = children.map(child => {
                    return {
                      branchId: path.id,
                      branchName: path.name,
                      id: child.id,
                      name: child.name
                    }
                  })
                  nodes.push({
                    id: path.id,
                    name: path.name,
                    children: nodeChildren
                  })
                }
              })
            }
            callback(null, nodes)
          }
        }
      }
    },
    setAllValuesSelected () {
      if (Array.isArray(this.pathToGetOptions)) {
        const options = this.optionsData.map(od => od.children)
        const optionsFlatted = options.flat()
        return this.$emit('input', optionsFlatted)
      }
      if (!this.childrenOf) {
        return this.$emit('input', this.optionsData)
      }
    }
  },
  mounted () {
    if (this.childrenOf && this.context) {
      this.$watch(`context.${this.childrenOf}`, async (val, oldVal) => {
        if (val) {
          const options = await commonsApi.axiosGetWrapper(`${this.pathToGetOptions}/${val.id}`)
          this.optionsFromPath = options.data.items
        }
        if (oldVal) {
          this.$emit('input', null)
        }
      })
    }
  }
}
</script>

<style>
.vue-treeselect .vue-treeselect__control {
  height: calc(1.5em + 0.75rem + 6px);
}

.vue-treeselect.is_invalid .vue-treeselect__control {
  border-color: #dc3545;
}

.vue-treeselect .vue-treeselect__label {
  display: -webkit-box;
  white-space: initial;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin: 0.25rem 0;
}

.vue-treeselect__placeholder,
.vue-treeselect__single-value {
  line-height: 37px !important;
}
</style>
